import { tv } from "tailwind-variants";

const pageButtonsVariants = tv({
  slots: {
    button: [
      "inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-sm tabular-nums",
      "transition-colors duration-150",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none",
    ],
  },
  variants: {
    /*
      A solid accent block read as the loudest thing in the footer. aria-current
      carries the semantics, so the current page only needs to look selected.
    */
    isActive: {
      true: {
        /*
          The ring is opaque, not accent/40: it is the only thing marking which
          page is current, so it has to clear 3:1 against the footer (SC 1.4.11).
          A translucent ring landed near 1.7:1.
        */
        button:
          "bg-accent-subtle font-semibold text-accent-on-subtle ring-1 ring-accent ring-inset",
      },
      false: { button: "text-content-muted hover:bg-surface-hover hover:text-content" },
    },
  },
});

const { button } = pageButtonsVariants();

type PageButtonsProps = {
  currentPage: number;
  numPages: number;
  setPage: (p: number) => void;
};

const getNumberList = (sentinel: number, currentPage: number, numPages: number) => {
  if (numPages <= 5) {
    return [...Array(numPages).keys()].map((k) => {
      return k + 1;
    });
  }

  if ([1, 2, numPages - 1, numPages].includes(currentPage)) {
    return [1, 2, sentinel, numPages - 1, numPages];
  }

  if (currentPage === 3) {
    return [1, 2, 3, sentinel, numPages - 1, numPages];
  }

  if (currentPage === numPages - 2) {
    return [1, 2, sentinel, numPages - 2, numPages - 1, numPages];
  }

  return [1, 2, sentinel, currentPage, sentinel, numPages - 1, numPages];
};

export const PageButtons = ({ currentPage, numPages, setPage }: PageButtonsProps) => {
  // Sentinel is mapped to "..." instead of a button
  const sentinel = 0;
  const pageNumbers = getNumberList(sentinel, currentPage, numPages);

  return (
    <ul className="flex flex-row items-center gap-1">
      {pageNumbers.map((num, index) => {
        const isActive = currentPage === num;

        return (
          <li key={`${num}-${index}`}>
            {num === sentinel ? (
              <span
                aria-hidden="true"
                className="flex size-8 items-center justify-center text-content-subtle"
              >
                …
              </span>
            ) : (
              <button
                /*
                  aria-current belongs on the control itself, and the number alone
                  is not a usable accessible name out of context.
                */
                aria-current={isActive ? "page" : undefined}
                aria-label={`Go to page ${num}`}
                className={button({ isActive })}
                onClick={() => {
                  return setPage(num);
                }}
                type="button"
              >
                {num}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
